'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

// ── Animated counter hook ──────────────────────────────────────────────────
function useCounter(target: number, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return value;
}

// ── Intersection observer hook ─────────────────────────────────────────────
function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ value, suffix, label, inView }: { value: number; suffix: string; label: string; inView: boolean }) {
  const count = useCounter(value, 1600, inView);
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-4xl font-bold text-primary font-mono">
        {count}{suffix}
      </span>
      <span className="text-sm text-muted-foreground text-center">{label}</span>
    </div>
  );
}

// ── Main landing page ──────────────────────────────────────────────────────
export default function LandingPage() {
  const statsSection = useInView(0.3);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">

      {/* ── NAV ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Image
              src="/assets/images/7dac5eeb-23b9-40ef-9d67-4c18e3a24583-1784715372916.png"
              alt="NautEX logo"
              width={32}
              height={32}
              className="rounded"
              priority
            />
            <span className="text-lg font-bold tracking-tight text-foreground">NautEX</span>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              href="/login"
              className="px-4 py-2 text-sm font-medium text-secondary-foreground hover:text-foreground transition-colors"
            >
              Sign In
            </Link>
            <Link
              href="/register/buyer"
              className="px-4 py-2 text-sm font-medium btn-secondary rounded-lg"
            >
              Register as Buyer
            </Link>
            <Link
              href="/register/supplier"
              className="px-4 py-2 text-sm font-medium btn-primary rounded-lg"
            >
              Register as Supplier
            </Link>
            <Link
              href="/register/broker"
              className="px-4 py-2 text-sm font-medium rounded-lg border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors"
            >
              Register as Broker
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-secondary-foreground hover:text-foreground"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {menuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-border/50 bg-card px-6 py-4 flex flex-col gap-3">
            <Link href="/login" className="text-sm font-medium text-secondary-foreground hover:text-foreground py-2" onClick={() => setMenuOpen(false)}>Sign In</Link>
            <Link href="/register/buyer" className="text-sm font-medium btn-secondary rounded-lg px-4 py-2 text-center" onClick={() => setMenuOpen(false)}>Register as Buyer</Link>
            <Link href="/register/supplier" className="text-sm font-medium btn-primary rounded-lg px-4 py-2 text-center" onClick={() => setMenuOpen(false)}>Register as Supplier</Link>
            <Link href="/register/broker" className="text-sm font-medium rounded-lg px-4 py-2 text-center border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors" onClick={() => setMenuOpen(false)}>Register as Broker</Link>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden">
        {/* Background radial glows */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/8 blur-[100px]" />
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/assets/images/7dac5eeb-23b9-40ef-9d67-4c18e3a24583-1784715372916.png"
              alt="NautEX Logo"
              className="h-36 w-auto object-contain"
              style={{ background: 'transparent', mixBlendMode: 'lighten' }}
            />
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/8 text-primary text-xs font-medium mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            Maritime Bunker Procurement Platform
          </div>

          <h1 className="text-5xl md:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            Bunker procurement,{' '}
            <span className="relative inline-block">
              <span className="text-primary">built for the sea.</span>
              <svg className="absolute -bottom-2 left-0 w-full" height="6" viewBox="0 0 300 6" fill="none" preserveAspectRatio="none">
                <path d="M0 3 Q75 0 150 3 Q225 6 300 3" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5" />
              </svg>
            </span>
          </h1>

          <p className="text-lg md:text-xl text-secondary-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
            NautEX connects vessel operators with verified bunker suppliers through a structured, compliant procurement workflow — from purchase request to delivery confirmation.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/register/buyer"
              className="btn-secondary px-8 py-3.5 rounded-xl text-base font-semibold w-full sm:w-auto text-center"
            >
              Register as Buyer
            </Link>
            <Link
              href="/register/supplier"
              className="btn-primary px-8 py-3.5 rounded-xl text-base font-semibold w-full sm:w-auto text-center"
            >
              Register as Supplier
            </Link>
            <Link
              href="/register/broker"
              className="px-8 py-3.5 rounded-xl text-base font-semibold w-full sm:w-auto text-center border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors"
            >
              Register as Broker
            </Link>
            <Link
              href="/login"
              className="text-sm font-medium text-secondary-foreground hover:text-foreground transition-colors underline underline-offset-4 w-full sm:w-auto text-center"
            >
              Already have an account? Sign in →
            </Link>
          </div>
        </div>

        {/* Hero visual — platform mockup strip */}
        <div className="relative max-w-4xl mx-auto mt-16">
          <div className="card-surface rounded-2xl overflow-hidden border border-border/60 shadow-2xl shadow-black/40">
            {/* Fake browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60 bg-secondary/30">
              <div className="w-3 h-3 rounded-full bg-status-rejected/60" />
              <div className="w-3 h-3 rounded-full bg-status-pending/60" />
              <div className="w-3 h-3 rounded-full bg-status-active/60" />
              <div className="flex-1 mx-4 h-5 rounded bg-muted/40 text-xs text-muted-foreground flex items-center px-3 font-mono">
                nautex.tech/purchase-requests
              </div>
            </div>
            {/* Fake dashboard rows */}
            <div className="p-6 space-y-3">
              {[
                { id: 'PR-2024-0891', vessel: 'MV Atlantic Star', port: 'Rotterdam', qty: '1,200 MT', status: 'Quoted', color: 'text-status-active' },
                { id: 'PR-2024-0890', vessel: 'MV Pacific Dawn', port: 'Singapore', qty: '800 MT', status: 'Awarded', color: 'text-primary' },
                { id: 'PR-2024-0889', vessel: 'MV Nordic Wind', port: 'Fujairah', qty: '2,000 MT', status: 'Pending', color: 'text-status-pending' },
              ].map((row) => (
                <div key={row.id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted/20 border border-border/40 table-row-hover">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-mono text-muted-foreground w-28 hidden sm:block">{row.id}</span>
                    <span className="text-sm font-medium text-foreground">{row.vessel}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="text-xs text-muted-foreground hidden md:block">{row.port}</span>
                    <span className="text-xs font-mono text-secondary-foreground hidden sm:block">{row.qty}</span>
                    <span className={`text-xs font-semibold ${row.color}`}>{row.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Glow under card */}
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-primary/10 blur-2xl rounded-full" />
        </div>
      </section>

      {/* ── STATS ── */}
      <section ref={statsSection.ref} className="py-16 px-6 border-y border-border/40">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          <StatCard value={500} suffix="+" label="Verified Suppliers" inView={statsSection.inView} />
          <StatCard value={120} suffix="+" label="Active Buyer Offices" inView={statsSection.inView} />
          <StatCard value={98} suffix="%" label="On-Time Delivery Rate" inView={statsSection.inView} />
          <StatCard value={40} suffix="+" label="Ports Covered" inView={statsSection.inView} />
        </div>
      </section>

      {/* ── VALUE PROPS — BENTO GRID ── */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Platform Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              One platform. Every step of the deal.
            </h2>
          </div>

          {/* Bento grid — asymmetric */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Large card — spans 2 cols */}
            <div className="md:col-span-2 card-surface rounded-2xl p-8 relative overflow-hidden group hover:border-primary/40 transition-colors duration-300">
              <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors duration-500" />
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center mb-5">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Structured Purchase Requests</h3>
                <p className="text-secondary-foreground leading-relaxed mb-5">
                  Buyers submit detailed purchase requests with vessel specs, port, quantity, and delivery window. The platform routes them to eligible, pre-screened suppliers automatically — no email chains, no missed bids.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Vessel Registry', 'Port Scheduling', 'Multi-Grade Support', 'Deadline Enforcement'].map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-muted/50 text-xs text-secondary-foreground border border-border/60">{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Tall card */}
            <div className="card-surface rounded-2xl p-8 relative overflow-hidden group hover:border-primary/40 transition-colors duration-300">
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-accent/8 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center mb-5">
                  <svg className="w-5 h-5 text-accent-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">Competitive Quote Comparison</h3>
                <p className="text-secondary-foreground leading-relaxed">
                  Receive multiple quotes side-by-side. Compare price, delivery terms, and supplier rating before awarding. Full audit trail on every decision.
                </p>
              </div>
            </div>

            {/* Small card */}
            <div className="card-surface rounded-2xl p-8 relative overflow-hidden group hover:border-primary/40 transition-colors duration-300">
              <div className="w-10 h-10 rounded-xl bg-status-active/15 flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-status-active" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Sanctions Screening</h3>
              <p className="text-sm text-secondary-foreground leading-relaxed">
                Every entity is screened against international sanctions lists before activation. Compliance built in, not bolted on.
              </p>
            </div>

            {/* Wide card — spans 2 cols */}
            <div className="md:col-span-2 card-surface rounded-2xl p-8 relative overflow-hidden group hover:border-primary/40 transition-colors duration-300">
              <div className="absolute top-0 right-0 w-56 h-56 bg-primary/4 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center mb-5">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">End-to-End PO & Delivery Lifecycle</h3>
                <p className="text-secondary-foreground leading-relaxed mb-5">
                  From award to delivery confirmation — purchase orders, delivery schedules, BDN uploads, non-conformance reports, and dispute resolution are all managed inside the platform. Nothing falls through the cracks.
                </p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
                  <span className="text-primary">PR</span>
                  <span>→</span>
                  <span>RFQ</span>
                  <span>→</span>
                  <span>Quote</span>
                  <span>→</span>
                  <span>Award</span>
                  <span>→</span>
                  <span>PO</span>
                  <span>→</span>
                  <span className="text-status-active">Delivery</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── FOR BUYERS / FOR SUPPLIERS — SPLIT ── */}
      <section className="py-24 px-6 border-t border-border/40">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Buyers */}
            <div className="rounded-2xl p-8 border border-border/60 bg-gradient-to-br from-secondary/60 to-card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/6 rounded-full blur-3xl" />
              <div className="relative">
                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-4">For Buyers</p>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Procure bunkers with confidence
                </h3>
                <p className="text-secondary-foreground mb-6 leading-relaxed">
                  Manage your entire fleet's fuel procurement from one dashboard. Submit requests, compare supplier quotes, track delivery status, and maintain a complete audit trail — all without leaving the platform.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Multi-vessel, multi-port purchase requests',
                    'Side-by-side quote comparison with full pricing breakdown',
                    'Preferred supplier relationships and history',
                    'Delivery confirmation and BDN management',
                    'Office-level reporting and commission visibility',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-secondary-foreground">
                      <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/register/buyer"
                  className="btn-secondary inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold"
                >
                  Register as Buyer
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Suppliers */}
            <div className="rounded-2xl p-8 border border-primary/25 bg-gradient-to-br from-primary/8 to-card relative overflow-hidden">
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-primary/8 rounded-full blur-3xl" />
              <div className="relative">
                <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-4">For Suppliers</p>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Reach qualified buyers at scale
                </h3>
                <p className="text-secondary-foreground mb-6 leading-relaxed">
                  Get invited to bid on purchase requests that match your port coverage and product capabilities. Submit competitive quotes, manage delivery schedules, and grow your business with verified buyers.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Targeted RFQ invitations based on your capabilities',
                    'Structured quote submission with pricing and terms',
                    'Delivery schedule management and BDN uploads',
                    'Performance history and preferred supplier status',
                    'Transparent commission and payment tracking',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-secondary-foreground">
                      <svg className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/register/supplier"
                  className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold"
                >
                  Register as Supplier
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Brokers — full width */}
            <div className="lg:col-span-2 rounded-2xl p-8 border border-amber-500/25 bg-gradient-to-br from-amber-500/8 to-card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-56 h-40 bg-amber-500/6 rounded-full blur-3xl" />
              <div className="relative">
                <p className="text-xs font-semibold text-amber-400 uppercase tracking-widest mb-4">For Brokers</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-2xl font-bold text-foreground mb-4">
                      Intermediate deals and earn commission
                    </h3>
                    <p className="text-secondary-foreground mb-6 leading-relaxed">
                      Register as a broker to raise purchase requests on behalf of buyers, manage arrangements between buyers and suppliers, and earn transparent commission on every completed deal.
                    </p>
                    <Link
                      href="/register/broker"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors"
                    >
                      Register as Broker
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Raise purchase requests on behalf of buyers',
                      'Manage buyer–supplier arrangements',
                      'Track commission on every deal',
                      'Full visibility into PO and delivery status',
                      'Dedicated broker dashboard and reporting',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-secondary-foreground">
                        <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — HORIZONTAL FLOW ── */}
      <section className="py-24 px-6 border-t border-border/40">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-3">Procurement Workflow</p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">How a deal moves through NautEX</h2>
          </div>

          <div className="relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-border to-transparent" />

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { step: '01', title: 'Submit PR', desc: 'Buyer creates a purchase request with vessel, port, grade, and quantity.' },
                { step: '02', title: 'RFQ Dispatch', desc: 'Platform invites eligible suppliers based on port and product capabilities.' },
                { step: '03', title: 'Quote & Compare', desc: 'Suppliers submit quotes. Buyer compares and selects the best offer.' },
                { step: '04', title: 'PO Issued', desc: 'Purchase order is generated and sent to the awarded supplier.' },
                { step: '05', title: 'Delivery & Close', desc: 'Supplier confirms delivery. BDN uploaded. Deal closed with full audit trail.' },
              ].map((item) => (
                <div key={item.step} className="flex flex-col items-center text-center gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-card border border-border/60 flex items-center justify-center relative z-10">
                    <span className="text-lg font-bold font-mono text-primary">{item.step}</span>
                  </div>
                  <h4 className="text-sm font-bold text-foreground">{item.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden border border-primary/25 bg-gradient-to-br from-primary/10 via-secondary/40 to-card p-12 text-center">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-primary/10 blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to streamline your bunker procurement?
              </h2>
              <p className="text-secondary-foreground mb-8 max-w-xl mx-auto">
                Join the growing network of vessel operators and bunker suppliers on NautEX. Registration is free and takes under 10 minutes.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link href="/register/buyer" className="btn-secondary px-8 py-3.5 rounded-xl text-base font-semibold w-full sm:w-auto text-center">
                  Register as Buyer
                </Link>
                <Link href="/register/supplier" className="btn-primary px-8 py-3.5 rounded-xl text-base font-semibold w-full sm:w-auto text-center">
                  Register as Supplier
                </Link>
                <Link href="/register/broker" className="px-8 py-3.5 rounded-xl text-base font-semibold w-full sm:w-auto text-center border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 transition-colors">
                  Register as Broker
                </Link>
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                Already registered?{' '}
                <Link href="/login" className="text-primary hover:underline underline-offset-4">
                  Sign in to your account →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="border-t border-border/40 py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Image src="/assets/images/7dac5eeb-23b9-40ef-9d67-4c18e3a24583-1784715372916.png" alt="NautEX" width={24} height={24} className="rounded opacity-70" />
            <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} NautEX. Maritime Bunker Procurement.</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link href="/login" className="hover:text-foreground transition-colors">Sign In</Link>
            <Link href="/register/buyer" className="hover:text-foreground transition-colors">Buyer Registration</Link>
            <Link href="/register/supplier" className="hover:text-foreground transition-colors">Supplier Registration</Link>
            <Link href="/register/broker" className="hover:text-foreground transition-colors">Broker Registration</Link>
            <Link href="/admin-portal-login" className="hover:text-foreground transition-colors text-xs opacity-40 hover:opacity-70">Staff Portal</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}